import React, { createContext, useState, useEffect, useMemo } from 'react';
import { useLayoutNavigation } from '../../hooks/layout-navigation-hook';
import { useOktaAuth } from '@okta/okta-react';
import { useLocation } from 'react-router-dom';
import { startOfWeek } from 'date-fns';
import store from '../../store/store';
import Loader from '../Loader/Loader';
import { FiltersLoaderContainer } from './style';
import { getStartOfMonth, getEndOfMonth } from './helper';

export const FilterContext = createContext();

export const FilterProvider = ({ children }) => {
  const {
    getFiltersData,
    filtersData,
    getUserRoles,
    userInitial,
    getGSPPFiltersData,
    gsppFiltersData,
    filtersLoading,
  } = useLayoutNavigation();
  const oktaContext = useOktaAuth();
  const authState = oktaContext?.authState;
  const isMFE = store.getState().app.isMFE;
  const isAuthenticated = authState?.isAuthenticated || isMFE;

  const [selectedFilters, setSelectedFilters] = useState({
    selectedProduct: null,
    selectedModel: null,
    selectedSector: null,
    selectedMarket: null,
    selectedPlant: null,
  });
  const [transformedData, setTransformedData] = useState({});
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedSector, setSelectedSector] = useState(null);
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedRetailer, setSelectedRetailer] = useState({});
  const [selectedPPRegion, setSelectedPPRegion] = useState({});
  const [selectedZone, setSelectedZone] = useState({});
  const [selectedDistrict, setSelectedDistrict] = useState({});
  const [categories, setSelectedCategory] = useState(null);
  const [selectedApplicationNames, setSelectedApplicaitonNames] =
    useState(null);
  const [selectedPlant, setSelectedPlant] = useState(null);
  const [selectedTab, setSelectedTab] = useState({});
  const [currentTabName, setCurrentTabName] = useState(null);
  const [currentSubTabName, setCurrentSubTabName] = useState(null);
  const location = useLocation();

  // These states include current selected options for the tools and projects filter.
  const [selectedTool, setSelectedTool] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);

  // These states include all possible options for the tools and projects which used in clearAll function.
  const [toolAllOptions, setToolAllOptions] = useState([]);
  const [projectAllOptions, setProjectAllOptions] = useState([]);

  const [selectedStartDate, setSelectedStartDate] = useState(
    getStartOfMonth(new Date(), true)
    // new Date(Date.now() - 30 * 24 * 60 * 60 * 1000) // 30 days back
  );
  const [startUTCDate, setStartUTCDate] = useState(
    getStartOfMonth(new Date())?.toISOString()
    // new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString()
  );
  const [selectedEndDate, setSelectedEndDate] = useState(
    getEndOfMonth(new Date())
  );
  const [endUTCDate, setEndUTCDate] = useState(
    getEndOfMonth(new Date()).toISOString()
  );
  const [ytdCheckbox, setYTDCheckbox] = useState(true);
  const [betaFeatureToggle, setBetaFeatureToggle] = useState(false);
  const [tabWeeklyToggle, setTabWeeklyToggle] = useState(false);

  const [transformedPPData, setTransformedPPData] = useState({});
  const [rgmPromoUserType, setRgmPromoUserType] = useState({
    value: 'All',
    label: 'All Users',
  });

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedWeek, setSelectedWeek] = useState(
    startOfWeek(new Date(), { weekStartsOn: 2 }) // 2 = Tuesday
  );
  const [goLiveDate, setGoLiveDate] = useState(new Date());

  // Frequency state - used across application for API calls
  const [selectedFrequency, setSelectedFrequency] = useState({
    id: 'Monthly',
    label: 'Monthly',
    config: {
      startDay: null,
      endDay: null,
      periodDays: 182,
    },
  });
  const [selectedFrequencyConfig, setSelectedFrequencyConfig] = useState({
    startDay: null,
    endDay: null,
    periodDays: 182,
  });

  useEffect(() => {
    getUserRoles();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      if (
        location.pathname === '/' ||
        location.pathname === '/dashboard' ||
        location.pathname === '/psd' ||
        location.pathname === '/mfe-test'
      ) {
        getFiltersData({
          variables: {},
        });
        getGSPPFiltersData({
          variables: {},
        });
      }
    }
  }, [getFiltersData, authState, getGSPPFiltersData, isMFE]);

  const getAllProducts = () => {
    const allProducts = filtersData?.programs?.reduce((acc, program) => {
      return acc.concat(
        program.products.map((product) => ({
          ...product,
          programId: program.programId,
          programName: program.programName,
          programFullName: program?.programFullName,
          id: product.productId,
          label: product.productName,
          value: product.productName,
          showModel: product.showModel,
        }))
      );
    }, []);
    return allProducts;
  };

  const getAllSectors = () => {
    const allSectors = filtersData?.programs?.reduce((acc, program) => {
      return acc.concat(
        program.products.reduce((prodAcc, product) => {
          return prodAcc.concat(
            product.models.reduce((modelAcc, model) => {
              return modelAcc.concat(
                model.sectors.map((sector) => ({
                  ...sector,
                  programId: program.programId,
                  productId: product.productId,
                  productName: product.productName,
                  modelId: model.modelId,
                  modelName: model.modelName,
                  id: sector.sectorId,
                  label: sector.sectorName,
                  value: sector.sectorName,
                }))
              );
            }, [])
          );
        }, [])
      );
    }, []);
    return allSectors;
  };

  const getAllMarkets = () => {
    const allMarkets = filtersData?.programs?.reduce((acc, program) => {
      return acc.concat(
        program.products.reduce((prodAcc, product) => {
          return prodAcc.concat(
            product.models.reduce((modelAcc, model) => {
              return modelAcc.concat(
                model.sectors.reduce((sectorAcc, sector) => {
                  return sectorAcc.concat(
                    sector.markets.map((market) => ({
                      ...market,
                      programId: program.programId,
                      productId: product.productId,
                      sectorId: sector.sectorId,
                      modelId: model.modelId,
                      id: market.marketId,
                      label: market.marketName,
                      value: market.marketName,
                    }))
                  );
                }, [])
              );
            }, [])
          );
        }, [])
      );
    }, []);
    return allMarkets;
  };

  const getAllModels = () => {
    let allModels = [];
    filtersData?.programs.forEach((program) => {
      // Iterate over products in each program
      program?.products.forEach((product) => {
        allModels = allModels.concat(
          product?.models.map((model) => ({
            label: model?.modelName,
            value: model?.modelName,
            id: model?.modelId,
            modelName: model?.modelName,
            goLiveDate: model?.goLiveDate,
            productId: product?.productId,
            programId: program?.programId,
            showSector: model.showSector,
            showPlant: model.showPlant,
            showMarket: model.showMarket,
          }))
        );
      });
    });

    return allModels;
  };

  const getAllApplications = () => {
    const allApplicationNames = filtersData?.programs?.reduce(
      (acc, program) => {
        return acc.concat(
          program.products.reduce((prodAcc, product) => {
            return prodAcc.concat(
              product.models.reduce((modelAcc, model) => {
                return modelAcc.concat(
                  model.sectors.reduce((sectorAcc, sector) => {
                    return sectorAcc.concat(
                      sector.markets.reduce((marketAcc, market) => {
                        return marketAcc.concat(
                          market.applicationNames.map((appName) => ({
                            platform: appName,
                            programId: program.programId,
                            productId: product.productId,
                            sectorId: sector.sectorId,
                            modelId: model.modelId,
                            marketId: market.marketId,
                          }))
                        );
                      }, [])
                    );
                  }, [])
                );
              }, [])
            );
          }, [])
        );
      },
      []
    );
    return allApplicationNames;
  };

  const getAllPlants = () => {
    const allSectors = filtersData?.programs?.reduce((acc, program) => {
      return acc.concat(
        program.products.reduce((prodAcc, product) => {
          return prodAcc.concat(
            product.models.reduce((modelAcc, model) => {
              return modelAcc.concat(
                model.sectors.flatMap((sector) => {
                  return sector?.plants?.map((plant) => {
                    return {
                      id: plant.plantId,
                      label: plant.plantName,
                      value: plant.plantName,
                      sectorId: sector.sectorId,
                      sectorName: sector.sectorName,
                      goLiveDate: plant?.goLiveDate,
                      productId: product.productId,
                      productName: product.productName,
                    };
                  });
                })
              );
            }, [])
          );
        }, [])
      );
    }, []);
    return allSectors;
  };

  useEffect(() => {
    if (Object.keys(filtersData).length > 0) {
      const updatedData = {
        programs: filtersData?.programs?.map(
          ({
            programId,
            programName,
            programFullName,
            products,
            showProduct,
          }) => ({
            programId,
            programName,
            programFullName,
            id: programId,
            label: programName,
            value: programName,
            showProduct,
            products,
          })
        ),
        products: getAllProducts(),
        models: getAllModels(),
        sectors: getAllSectors(),
        markets: getAllMarkets(),
        plants: getAllPlants(),
        applicationNames: getAllApplications(),
        categories: filtersData?.categories,
      };
      setSelectedCategory(filtersData?.categories);
      setTransformedData(updatedData);
    }
  }, [filtersData]);

  useEffect(() => {
    if (Object.keys(gsppFiltersData).length > 0) {
      const updatedData = {
        regions: gsppFiltersData[0]?.retailers[0].regions.map(
          (r) => r.regionName
        ),
        zones: gsppFiltersData[0]?.retailers[0].regions
          .map((r) => r.zones)[0]
          .map((z) => z.zoneName),
        districts: gsppFiltersData[0]?.retailers[0].regions.map(
          (r) => r.zones[0]
        )[0].districts,
      };
      setTransformedPPData(updatedData);
    }
  }, [gsppFiltersData]);

  const values = useMemo(
    () => ({
      regionValues: transformedData?.programs ?? [],
      selectedProgram,
      setSelectedProgram,
      productValues: transformedData?.products ?? [],
      selectedProduct,
      setSelectedProduct,
      modelValues: transformedData?.models ?? [],
      selectedModel,
      setSelectedModel,
      sectorValues: transformedData?.sectors ?? [],
      selectedSector,
      setSelectedSector,
      marketValues: transformedData?.markets ?? [],
      selectedMarket,
      setSelectedMarket,
      selectedCustomer,
      setSelectedCustomer,
      selectedRetailer,
      setSelectedRetailer,
      selectedPPRegion,
      setSelectedPPRegion,
      selectedZone,
      setSelectedZone,
      selectedDistrict,
      setSelectedDistrict,
      selectedStartDate,
      setSelectedStartDate,
      startUTCDate,
      setStartUTCDate,
      selectedEndDate,
      setSelectedEndDate,
      endUTCDate,
      setEndUTCDate,
      categories,
      setSelectedCategory,
      selectedApplicationNames,
      setSelectedApplicaitonNames,
      selectedTool,
      setSelectedTool,
      selectedProject,
      setSelectedProject,
      toolAllOptions,
      setToolAllOptions,
      projectAllOptions,
      setProjectAllOptions,
      allApplicationNames: transformedData?.applicationNames ?? [],
      userInitial,
      selectedFilters,
      setSelectedFilters,
      ytdCheckbox,
      setYTDCheckbox,
      betaFeatureToggle,
      setBetaFeatureToggle,
      tabWeeklyToggle,
      setTabWeeklyToggle,
      allRegions: transformedPPData?.regions ?? [],
      allZones: transformedPPData?.zones ?? [],
      allDistricts: transformedPPData?.districts ?? [],
      plantValues: transformedData?.plants ?? [],
      setSelectedPlant,
      selectedPlant,
      rgmPromoUserType,
      setRgmPromoUserType,
      selectedTab,
      setSelectedTab,
      currentTabName,
      setCurrentTabName,
      currentSubTabName,
      setCurrentSubTabName,
      selectedYear,
      setSelectedYear,
      selectedDate,
      setSelectedDate,
      selectedWeek,
      setSelectedWeek,
      goLiveDate,
      setGoLiveDate,
      filtersLoading,
      filtersData,
      selectedFrequency,
      setSelectedFrequency,
      selectedFrequencyConfig,
      setSelectedFrequencyConfig,
    }),
    [
      transformedData,
      selectedProgram,
      setSelectedProgram,
      selectedProduct,
      setSelectedProduct,
      selectedModel,
      setSelectedModel,
      selectedSector,
      setSelectedSector,
      selectedMarket,
      setSelectedMarket,
      selectedCustomer,
      setSelectedCustomer,
      selectedRetailer,
      setSelectedRetailer,
      selectedPPRegion,
      setSelectedPPRegion,
      selectedZone,
      setSelectedZone,
      selectedDistrict,
      setSelectedDistrict,
      selectedStartDate,
      setSelectedStartDate,
      startUTCDate,
      setStartUTCDate,
      selectedEndDate,
      setSelectedEndDate,
      endUTCDate,
      setEndUTCDate,
      categories,
      setSelectedCategory,
      selectedApplicationNames,
      setSelectedApplicaitonNames,
      selectedTool,
      setSelectedTool,
      selectedProject,
      setSelectedProject,
      toolAllOptions,
      setToolAllOptions,
      projectAllOptions,
      setProjectAllOptions,
      transformedData?.applicationNames,
      userInitial,
      selectedFilters,
      setSelectedFilters,
      ytdCheckbox,
      setYTDCheckbox,
      betaFeatureToggle,
      setBetaFeatureToggle,
      tabWeeklyToggle,
      setTabWeeklyToggle,
      transformedPPData?.regions,
      transformedPPData?.zones,
      transformedPPData?.districts,
      transformedData?.plants,
      setSelectedPlant,
      selectedPlant,
      rgmPromoUserType,
      setRgmPromoUserType,
      selectedTab,
      setSelectedTab,
      currentTabName,
      setCurrentTabName,
      currentSubTabName,
      setCurrentSubTabName,
      selectedYear,
      setSelectedYear,
      selectedDate,
      setSelectedDate,
      selectedWeek,
      setSelectedWeek,
      goLiveDate,
      setGoLiveDate,
      filtersLoading,
      filtersData,
      selectedFrequency,
      setSelectedFrequency,
      selectedFrequencyConfig,
      setSelectedFrequencyConfig,
    ]
  );

  if (filtersLoading) {
    return (
      <FiltersLoaderContainer>
        <Loader />
      </FiltersLoaderContainer>
    );
  }

  return (
    <FilterContext.Provider value={values}>{children}</FilterContext.Provider>
  );
};
